import { templateManager } from "@core/service-container";
import { Template, TemplateItem } from "@domain/template/template";
import { useComputedObservable, useObservable } from "micro-observables";
import { useCallback, useEffect, useState } from "react";

export function useTemplates(withFetch = true): {
	loading: boolean;
	templates: TemplateItem[] | undefined;
	error: unknown;
	refreshTemplates: () => Promise<void>;
} {
	const [loading, setLoading] = useState(withFetch);
	const [error, setError] = useState<unknown>(undefined);

	const templates = useObservable(templateManager.templateItems);

	const refreshTemplates = useCallback(async () => {
		try {
			await templateManager.fetchAllTemplates();
			setError(null);
		} catch (e) {
			setError(e);
			console.error(e);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		withFetch && refreshTemplates();
	}, [refreshTemplates, withFetch]);

	return { loading, templates, error, refreshTemplates };
}

export function useTemplate(
	templateId?: string,
	withFetch = true,
): {
	loading: boolean;
	template: Template | undefined;
	error: unknown;
	refreshTemplate: (templateId: string) => Promise<void>;
} {
	const [loading, setLoading] = useState(withFetch && !!templateId);
	const [error, setError] = useState<unknown>();

	const template = useComputedObservable<Template | undefined>(
		() => (templateId ? templateManager.templateById.get().get(templateId) : undefined),
		[templateId],
	);

	const refreshTemplate = useCallback(async (templateIdToRefresh: string) => {
		try {
			setLoading(true);
			await templateManager.fetchTemplate(templateIdToRefresh);
			setError(undefined);
		} catch (e) {
			setError(e);
			console.error(e);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		withFetch && templateId && refreshTemplate(templateId);
	}, [templateId, refreshTemplate, withFetch]);

	return { loading, template, error, refreshTemplate };
}
